import { useEffect, useState, type FormEvent } from "react";
import type { Profile, TravelPace } from "../types";
import type { AccountSession } from "../auth";
import type { SyncStatus } from "../useAccountSync";
import { WEBMCP_TOOL_COUNT, type WebMCPStatus } from "../webmcp";
import { CityCombobox } from "./CityCombobox";
import { Dialog } from "./Dialog";
import { Icon } from "./Icon";

interface ProfileDialogProps {
  open: boolean;
  profile: Profile;
  webMCPStatus: WebMCPStatus;
  webMCPMessage: string;
  onClose: () => void;
  onSave: (profile: Profile) => void;
  onReset: () => void;
  account: AccountSession | null;
  accountPending: boolean;
  syncStatus: SyncStatus;
  syncMessage: string;
  onSignIn: () => void;
  onSignOut: () => void;
  onDeleteCloud: () => void;
}

const currencies = ["USD", "EUR", "GBP", "JPY", "VND", "CAD", "AUD", "SGD", "KRW", "THB"];

const browserToolLabels: Record<WebMCPStatus, string> = {
  checking: "Checking browser support",
  ready: `${WEBMCP_TOOL_COUNT} browser tools ready`,
  unavailable: "Browser tools unavailable here",
  error: "Browser tools need attention",
};

export function ProfileDialog({ open, profile, webMCPStatus, webMCPMessage, onClose, onSave, onReset, account, accountPending, syncStatus, syncMessage, onSignIn, onSignOut, onDeleteCloud }: ProfileDialogProps) {
  const [form, setForm] = useState(profile);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setForm(profile); setError(""); }
  }, [open, profile]);

  function submit(event: FormEvent) {
    event.preventDefault();
    onSave({
      ...form,
      name: form.name.trim(),
      homeCity: form.homeCity.trim(),
      routingConsentAt: form.onlineRoutingEnabled
        ? form.routingConsentAt ?? new Date().toISOString()
        : undefined,
    });
    onClose();
  }

  return (
    <Dialog open={open} title="Travel profile" description="Roam stays local-first. Account sync is optional." onClose={onClose}>
      <form className="dialog-form" onSubmit={submit}>
        <fieldset className="dialog-section"><legend>CodePawl account</legend>
          {account ? (
            <div className="account-settings">
              <div className="account-identity"><span className="profile-initial">{account.user.name.trim().charAt(0).toUpperCase() || "R"}</span><span><strong>{account.user.name}</strong><small>{account.user.email}</small></span></div>
              <div className={`account-sync-state sync-${syncStatus}`}><Icon name="cloud" size={17} /><span><strong>{syncStatus === "ready" ? "Account sync ready" : syncStatus === "saving" ? "Saving account data" : syncStatus === "conflict" ? "Sync choice required" : "Account sync"}</strong><small>{syncMessage}</small></span></div>
              <div className="account-settings-actions"><a className="button button-secondary" href="https://codepawl.com/account/" target="_blank" rel="noreferrer">Manage account <Icon name="external" size={14} /></a><button className="button button-quiet" type="button" onClick={onSignOut}><Icon name="logout" size={16} /> Sign out</button></div>
              <button className="account-delete-sync" type="button" onClick={onDeleteCloud}>Delete synced Roam data</button>
            </div>
          ) : (
            <div className="account-signin-row"><span><strong>{accountPending ? "Checking your account…" : "Use Roam on more than one device"}</strong><small>Your current browser remains usable without an account.</small></span><button className="button button-primary" type="button" onClick={onSignIn} disabled={accountPending}><Icon name="login" size={16} /> Sign in</button></div>
          )}
        </fieldset>
        <fieldset className="dialog-section"><legend>Profile and defaults</legend><div className="field-grid">
          <label className="field field-span"><span>Name <small>optional</small></span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
          <CityCombobox className="field-span" label="Home city (optional)" value={form.homeCity} onChange={(homeCity) => setForm({ ...form, homeCity })} />
          <label className="field"><span>Default pace</span><select value={form.pace} onChange={(event) => setForm({ ...form, pace: event.target.value as TravelPace })}><option value="relaxed">Relaxed</option><option value="balanced">Balanced</option><option value="packed">Packed</option></select></label>
          <label className="field"><span>Currency</span><select value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value })}>{currencies.map((currency) => <option key={currency}>{currency}</option>)}</select></label>
        </div></fieldset>
        <fieldset className="dialog-section"><legend>Online route planning</legend>
          <label className="routing-consent-option">
            <input
              type="checkbox"
              checked={Boolean(form.onlineRoutingEnabled)}
              onChange={(event) => setForm({
                ...form,
                onlineRoutingEnabled: event.target.checked,
                routingConsentAt: event.target.checked ? form.routingConsentAt : undefined,
              })}
            />
            <span><strong>Enable real routes</strong><small>Calculate walking, cycling and driving routes with OpenRouteService by HeiGIT.</small></span>
          </label>
          <p className="dialog-help">When enabled, Roam sends stop coordinates to HeiGIT. Google receives an origin and destination only after you click Navigate. Transit still uses a buffer you provide.</p>
        </fieldset>
        <details className="settings-advanced">
          <summary><span><Icon name="more" size={17} />Advanced</span><Icon name="chevron-down" size={16} /></summary>
          <div className="settings-tool-status">
            <span className={`tool-status status-${webMCPStatus}`}><i aria-hidden="true" />{browserToolLabels[webMCPStatus]}</span>
            <p>{webMCPMessage}</p>
            <small>WebMCP is optional. It lets a supported browser agent use the same validated trip actions as the interface.</small>
          </div>
        </details>
        <p className="form-error" role="alert">{error}</p>
        <div className="dialog-split-actions">
          <div className="danger-zone"><span><strong>Reset Roam</strong><small>Delete this profile, every trip, and local activity.</small></span><button type="button" className="button button-danger-quiet" onClick={onReset}>Reset local data</button></div>
          <div className="dialog-actions"><button type="button" className="button button-secondary" onClick={onClose}>Cancel</button><button className="button button-primary" type="submit">Save profile</button></div>
        </div>
      </form>
    </Dialog>
  );
}
